import { useState, useCallback, useContext, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Search,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  EditOutlined,
  Close,
} from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import CustomPagination from '../../components/forms/Pagination';
import { UserContext } from '../../components/shared/useUser';
import { RecipeV2, RecipeV2Detail, RecipeComponent, RecipeStatus } from './types';
import { applyRoundAndFormat } from '../../utils';
import useRecipeV2List from './hooks/useRecipeV2List';
import useRecipeV2Api from './hooks/useRecipeV2Api';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import CarbonRatingChip from './components/CarbonRatingChip';
import AllergenChips from '../../components/shared/AllergenChips';
import BulkManageDialog from './components/BulkManageDialog';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

type VersionFilter = '' | 'current' | 'draft' | 'deprecated';
type StatusFilter = '' | 'active' | 'draft' | 'pendingApproval' | 'approved' | 'expired';

const statusColor: Record<RecipeStatus, 'success' | 'warning' | 'info' | 'default' | 'error'> = {
  [RecipeStatus.Active]: 'success',
  [RecipeStatus.Draft]: 'default',
  [RecipeStatus.PendingApproval]: 'warning',
  [RecipeStatus.Approved]: 'info',
  [RecipeStatus.Expired]: 'error',
};

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const MAX_EXPAND_DEPTH = 5;

/** Recursive component rows for nested sub-recipe expansion */
const ComponentRows = ({ components, depth, formatCurrency }: {
  components: RecipeComponent[];
  depth: number;
  formatCurrency: (value: number) => string;
}) => {
  const { t } = useTranslation('recipeV2');
  const [expandedSubs, setExpandedSubs] = useState<Set<number>>(new Set());

  const toggleSub = (id: number) => {
    setExpandedSubs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canExpand = (comp: RecipeComponent) => comp.isRecipe && comp.children?.length && depth < MAX_EXPAND_DEPTH;

  return (
    <>
      {components.map((comp, idx) => {
        const isSubExpanded = expandedSubs.has(comp.id);
        return (
          <Fragment key={comp.id}>
            <TableRow>
              <TableCell sx={{ width: 40, p: 0.5 }}>
                {canExpand(comp) ? (
                  <IconButton
                    size="small"
                    onClick={() => toggleSub(comp.id)}
                  >
                    {isSubExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                  </IconButton>
                ) : null}
              </TableCell>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography variant="textSM"
                  fontFamily="monospace">
                  {comp.componentItemNumber}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack direction="row"
                  spacing={1}
                  alignItems="center">
                  <span>{comp.componentDescription}</span>
                  {comp.isRecipe && (
                    <Chip label={t('build.subRecipe')}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }} />
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right">{comp.quantity}</TableCell>
              <TableCell>{comp.uom}</TableCell>
              <TableCell align="right">{comp.weightNet}g</TableCell>
              <TableCell align="right">{formatCurrency(comp.costPrice)}</TableCell>
              <TableCell align="right">{(comp.costContribution * 100).toFixed(1)}%</TableCell>
            </TableRow>
            {canExpand(comp) && isSubExpanded && (
              <TableRow>
                <TableCell colSpan={9}
                  sx={{ p: 0, pl: depth * 3, backgroundColor: `grey.${Math.min(50 + (depth - 1) * 50, 200)}` }}>
                  <Table size="small">
                    <TableBody>
                      <ComponentRows
                        components={comp.children!}
                        depth={depth + 1}
                        formatCurrency={formatCurrency}
                      />
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const RecipeV2Page = () => {
  const { t } = useTranslation('recipeV2');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Quick filters
  const [versionFilter, setVersionFilter] = useState<VersionFilter>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  // Advanced filters
  const [filterCategory, setFilterCategory] = useState('');
  const [advancedVersionFilter, setAdvancedVersionFilter] = useState<VersionFilter>('');
  const [advancedStatusFilter, setAdvancedStatusFilter] = useState<StatusFilter>('');

  const { recipes, totalCount, isLoading, error, loadRecipes } = useRecipeV2List();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const api = useRecipeV2Api();

  const hasWritePermission = hasPermissionTo(['RecipeV2Write']);
  const hasExportPermission = hasPermissionTo(['RecipeV2Export']);
  const hasBulkManagePermission = hasPermissionTo(['RecipeV2BulkManage']);
  const [bulkManageOpen, setBulkManageOpen] = useState(false);

  const handleExport = async () => {
    try {
      const ids = recipes.map((r) => r.id);
      const blob = await api.exportRecipes(ids, 'xlsx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recipes-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    loadRecipes(debouncedSearch, page, rowsPerPage);
  }, [debouncedSearch, page, rowsPerPage, loadRecipes]);

  // Debounce search input — only fires after 3+ characters or empty
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
        setDebouncedSearch(value);
        setPage(0);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Sync quick filters -> advanced filters
  useEffect(() => {
    setAdvancedVersionFilter(versionFilter);
  }, [versionFilter]);

  useEffect(() => {
    setAdvancedStatusFilter(statusFilter);
  }, [statusFilter]);

  // Sync advanced filters -> quick filters
  const handleAdvancedVersionChange = (value: VersionFilter) => {
    setAdvancedVersionFilter(value);
    setVersionFilter(value);
  };

  const handleAdvancedStatusChange = (value: StatusFilter) => {
    setAdvancedStatusFilter(value);
    setStatusFilter(value);
  };

  const handleClearFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVersionFilter('');
    setStatusFilter('');
    setAdvancedVersionFilter('');
    setAdvancedStatusFilter('');
    setFilterCategory('');
    setSearch('');
    setDebouncedSearch('');
  };

  const handleRowClick = useCallback(
    (recipe: RecipeV2) => {
      navigate(`/store/recipes-v2/${recipe.id}`);
    },
    [navigate],
  );

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedData, setExpandedData] = useState<Record<number, RecipeComponent[]>>({});
  const [expandLoading, setExpandLoading] = useState<Set<number>>(new Set());
  const recipeApi = useRecipeV2Api();

  const toggleRow = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Fetch components if not cached
        if (!expandedData[id]) {
          setExpandLoading(prev => new Set(prev).add(id));
          recipeApi.getRecipeDetail(id).then((detail: RecipeV2Detail) => {
            setExpandedData(prev => ({ ...prev, [id]: detail.components }));
            setExpandLoading(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }).catch(() => {
            setExpandLoading(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          });
        }
      }
      return next;
    });
  };

  const maxPages = Math.ceil(totalCount / rowsPerPage);
  const formatCurrency = (value: number) => applyRoundAndFormat(value, currencyCode);

  // Client-side quick filtering (in production these become API params)
  const filteredRecipes = recipes.filter((recipe) => {
    if (versionFilter === 'draft' && recipe.status !== RecipeStatus.Draft) return false;
    if (versionFilter === 'current' && recipe.status !== RecipeStatus.Active) return false;
    if (versionFilter === 'deprecated' && recipe.status !== RecipeStatus.Expired) return false;
    if (statusFilter === 'active' && recipe.status !== RecipeStatus.Active) return false;
    if (statusFilter === 'draft' && recipe.status !== RecipeStatus.Draft) return false;
    if (statusFilter === 'pendingApproval' && recipe.status !== RecipeStatus.PendingApproval) return false;
    if (statusFilter === 'approved' && recipe.status !== RecipeStatus.Approved) return false;
    if (statusFilter === 'expired' && recipe.status !== RecipeStatus.Expired) return false;
    if (filterCategory && recipe.primaryCategory !== filterCategory) return false;
    return true;
  });

  return (
    <PageArea>
      <Stack width="100%"
        spacing={2}>
        {/* Page header */}
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center">
          <PageTitle subTitle={t('subtitle')}>{t('title')}</PageTitle>
          <Stack direction="row"
            spacing={1}>
            {hasBulkManagePermission && (
              <Button
                variant="secondary"
                onClick={() => setBulkManageOpen(true)}
              >
                {t('bulkManage.title')}
              </Button>
            )}
            {hasExportPermission && (
              <Button
                variant="secondary"
                onClick={handleExport}
              >
                {t('export.export')}
              </Button>
            )}
            {hasWritePermission && (
              <Button
                variant="primary"
                startIcon={<Add />}
                onClick={() => navigate('/store/recipes-v2/new')}
              >
                {t('newRecipe')}
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Quick filters + search row */}
        <Grid container
          spacing={2}
          alignItems="center">
          {/* Search field */}
          <Grid item
            sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Close
                      sx={{ cursor: 'pointer', fontSize: 18 }}
                      onClick={() => handleSearchChange('')}
                    />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ minWidth: 240, mb: 0 }}
            />
          </Grid>

          <Grid item>
            <QuickFilterDivider />
          </Grid>

          {/* Version group */}
          <Grid item>
            <Grid container
              spacing={1}
              alignItems="center">
              <Grid item>
                <Chip
                  label={t('filters.current')}
                  color={versionFilter === 'current' ? 'primary' : 'default'}
                  onClick={() => setVersionFilter(prev => prev === 'current' ? '' : 'current')}
                />
              </Grid>
              <Grid item>
                <Chip
                  label={t('filters.draft')}
                  color={versionFilter === 'draft' ? 'primary' : 'default'}
                  onClick={() => setVersionFilter(prev => prev === 'draft' ? '' : 'draft')}
                />
              </Grid>
              <Grid item>
                <Chip
                  label={t('filters.deprecated')}
                  color={versionFilter === 'deprecated' ? 'primary' : 'default'}
                  onClick={() => setVersionFilter(prev => prev === 'deprecated' ? '' : 'deprecated')}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item>
            <QuickFilterDivider />
          </Grid>

          {/* Status group */}
          <Grid item>
            <Grid container
              spacing={1}
              alignItems="center">
              <Grid item>
                <Chip
                  label={t('status.active')}
                  color={statusFilter === 'active' ? 'primary' : 'default'}
                  onClick={() => setStatusFilter(prev => prev === 'active' ? '' : 'active')}
                />
              </Grid>
              <Grid item>
                <Chip
                  label={t('status.pendingApproval')}
                  color={statusFilter === 'pendingApproval' ? 'primary' : 'default'}
                  onClick={() => setStatusFilter(prev => prev === 'pendingApproval' ? '' : 'pendingApproval')}
                />
              </Grid>
              <Grid item>
                <Chip
                  label={t('status.approved')}
                  color={statusFilter === 'approved' ? 'primary' : 'default'}
                  onClick={() => setStatusFilter(prev => prev === 'approved' ? '' : 'approved')}
                />
              </Grid>
              <Grid item>
                <Chip
                  label={t('status.expired')}
                  color={statusFilter === 'expired' ? 'primary' : 'default'}
                  onClick={() => setStatusFilter(prev => prev === 'expired' ? '' : 'expired')}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Advanced filters accordion */}
        <Accordion
          variant="borderless"
          expanded={isAdvancedOpen}
          onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
        >
          <AccordionSummary>
            <Grid container
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}>
              <Grid item>
                <Grid container
                  spacing={1}
                  alignItems="center">
                  <Grid item>
                    {t('filters.advanced')}
                  </Grid>
                  <Grid item
                    style={{ marginTop: '8px' }}>
                    {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  size="md"
                  variant="secondary"
                  onClick={handleClearFilters}
                >
                  {t('filters.clear')}
                </Button>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container
              spacing={3}>
              <Grid item
                lg={3}
                sm={6}
                xs={12}>
                <TextField
                  select
                  label={t('filters.versionLabel')}
                  value={advancedVersionFilter}
                  onChange={(e) => handleAdvancedVersionChange(e.target.value as VersionFilter)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">{t('filters.all')}</MenuItem>
                  <MenuItem value="current">{t('filters.current')}</MenuItem>
                  <MenuItem value="draft">{t('filters.draft')}</MenuItem>
                  <MenuItem value="deprecated">{t('filters.deprecated')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item
                lg={3}
                sm={6}
                xs={12}>
                <TextField
                  select
                  label={t('statusLabel')}
                  value={advancedStatusFilter}
                  onChange={(e) => handleAdvancedStatusChange(e.target.value as StatusFilter)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">{t('filters.all')}</MenuItem>
                  <MenuItem value="active">{t('status.active')}</MenuItem>
                  <MenuItem value="draft">{t('status.draft')}</MenuItem>
                  <MenuItem value="pendingApproval">{t('status.pendingApproval')}</MenuItem>
                  <MenuItem value="approved">{t('status.approved')}</MenuItem>
                  <MenuItem value="expired">{t('status.expired')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item
                lg={3}
                sm={6}
                xs={12}>
                <TextField
                  label={t('category')}
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Table in Section container */}
        <Section>
          <TableHeader>
            {t('title')}
            {totalCount > 0 && <Pill text={`${filteredRecipes.length} ${t('title')}`} />}
          </TableHeader>

          {isLoading ? (
            <Stack alignItems="center"
              py={6}>
              <CircularProgress size={24} />
            </Stack>
          ) : error ? (
            <Stack alignItems="center"
              py={6}>
              <Typography color="error">{error}</Typography>
            </Stack>
          ) : filteredRecipes.length === 0 ? (
            <Stack alignItems="center"
              py={6}>
              <Typography color="text.secondary">{t('noRecipes')}</Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small"
                sx={{ minWidth: '1000px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('sku')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('name')}</TableCell>
                    <TableCell align="center"
                      sx={{ whiteSpace: 'nowrap' }}>{t('statusLabel')}</TableCell>
                    <TableCell align="center"
                      sx={{ whiteSpace: 'nowrap' }}>{t('version')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('category')}</TableCell>
                    <TableCell align="right"
                      sx={{ whiteSpace: 'nowrap' }}>{t('cost')}</TableCell>
                    <TableCell align="right"
                      sx={{ whiteSpace: 'nowrap' }}>{t('costPerPortion')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('allergensLabel')}</TableCell>
                    <TableCell align="center"
                      sx={{ whiteSpace: 'nowrap' }}>{t('carbonLabel')}</TableCell>
                    <TableCell align="center"
                      sx={{ whiteSpace: 'nowrap' }}>{t('actions.title')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecipes.map((recipe) => {
                    const isExpanded = expandedRows.has(recipe.id);
                    return (
                      <>
                        <TableRow
                          key={recipe.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(recipe)}
                        >
                          <TableCell sx={{ width: 40, p: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => toggleRow(recipe.id, e)}
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="textSM"
                              fontFamily="monospace">
                              {recipe.itemNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="textSM"
                              fontWeight={500}>
                              {recipe.description}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={t(`status.${recipe.status.charAt(0).toLowerCase() + recipe.status.slice(1)}`)}
                              color={statusColor[recipe.status] ?? 'default'}
                              size="small"
                              sx={{ px: '1px' }}
                            />
                          </TableCell>
                          <TableCell align="center">v{recipe.currentVersion}</TableCell>
                          <TableCell>{recipe.primaryCategory}</TableCell>
                          <TableCell align="right">{formatCurrency(recipe.totalCost)}</TableCell>
                          <TableCell align="right">{formatCurrency(recipe.costPerPortion)}</TableCell>
                          <TableCell>
                            <AllergenChips allergens={recipe.allergenSummary}
                              max={3} />
                          </TableCell>
                          <TableCell align="center">
                            {recipe.carbonScore && (
                              <CarbonRatingChip rating={recipe.carbonScore} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {hasWritePermission && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/store/recipes-v2/${recipe.id}`);
                                }}
                                aria-label={t('actions.edit')}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                        {/* Expandable detail row */}
                        {isExpanded && (
                          <TableRow key={`${recipe.id}-expand`}>
                            <TableCell
                              colSpan={11}
                              sx={{ p: 0 }}>
                              <Collapse in={isExpanded}
                                timeout={250}
                                unmountOnExit>
                                <Box sx={{ px: 6, py: 3, backgroundColor: 'grey.50' }}>
                                  {expandLoading.has(recipe.id) ? (
                                    <Stack alignItems="center"
                                      py={3}>
                                      <CircularProgress size={20} />
                                    </Stack>
                                  ) : expandedData[recipe.id]?.length ? (
                                    <Section style={{ boxShadow: 'none' }}>
                                      <Box sx={{ p: 5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="textMD"
                                          fontWeight={600}>
                                          {recipe.description} {t('build.componentsTitle', 'Components')}
                                        </Typography>
                                        <Pill text={`${expandedData[recipe.id].length}`} />
                                      </Box>
                                      <TableContainer>
                                        <Table size="small"
                                          sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 40 }} />
                                              <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>#</TableCell>
                                              <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{t('sku')}</TableCell>
                                              <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{t('name')}</TableCell>
                                              <TableCell align="right"
                                                sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{t('build.qty')}</TableCell>
                                              <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{t('build.uom')}</TableCell>
                                              <TableCell align="right"
                                                sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{t('build.weight')}</TableCell>
                                              <TableCell align="right"
                                                sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{t('cost')}</TableCell>
                                              <TableCell align="right"
                                                sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{t('build.costContribution')}</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            <ComponentRows
                                              components={expandedData[recipe.id]}
                                              depth={2}
                                              formatCurrency={formatCurrency}
                                            />
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </Section>
                                  ) : (
                                    <Typography variant="textSM"
                                      color="text.secondary">
                                      {t('noRecipes')}
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages}
            breakpointForChangeDisplay={120000}
            useNoDataMessage={false}
          />
        </Section>
      </Stack>

      <BulkManageDialog
        open={bulkManageOpen}
        onClose={() => setBulkManageOpen(false)}
        onSuccess={() => loadRecipes(debouncedSearch, page, rowsPerPage)}
      />
    </PageArea>
  );
};

export default RecipeV2Page;
